// ─── AdminWorkflowsTab — UI editable workflows multi-etapa (0641) ────────────
// Tab de admin que permite configurar el workflow ADR-025 multi-etapa por
// artefacto (ORGANIGRAMA, FLUJOGRAMA, DOC, PROCESO).
//
// Por ahora editable solo para ORGANIGRAMA (los demás artefactos se selecccionan
// pero usan el modelo legacy de aprobador único hasta que el flujo multi-etapa
// se generalice).
//
// Estructura:
//   - Selector artefactoTipo
//   - Lista de etapas en orden con badge "Etapa N · accion · modo"
//   - Cada etapa muestra los roles asociados (con filtro puesto opcional)
//   - Botones por etapa: cambiar modo · añadir rol · borrar etapa
//   - Modal lateral "Añadir rol" con dropdown tipo + dropdown puesto opcional
//   - Botón global "Añadir etapa"

import { useEffect, useState } from 'react';
import { C } from '../../../../lib/design';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import {
  fetchTiposRolGobernanza,
  fetchPuestos,
  fetchPuestosDeTipoRol,
  type TipoRolGobernanzaAdmin,
  type PuestoItem,
} from '../../../shared/services/api';
import {
  fetchEtapasConfig,
  crearEtapa,
  actualizarEtapa,
  borrarEtapa,
  anadirRolAEtapa,
  quitarRolDeEtapa,
  actualizarConfig,
  type ArtefactoTipo,
  type AccionEtapa,
  type ModoEtapa,
  type ConfigEtapas,
} from '../../../platform/workflow/workflow-config.service';
import { NotifPlantillaEditor } from '../components/NotifPlantillaEditor';

const ARTEFACTOS: { tipo: ArtefactoTipo; label: string }[] = [
  { tipo: 'ORGANIGRAMA', label: 'Organigrama' },
  { tipo: 'FLUJOGRAMA', label: 'Flujograma' },
  { tipo: 'DOC', label: 'Documento' },
  { tipo: 'PROCESO', label: 'Proceso' },
];

const ROLES_ADMIN_EDITORS = ['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN'];

interface AdminWorkflowsTabProps {
  /**
   * Si se especifica, oculta el selector de artefacto y muestra solo el
   * workflow del artefacto fijo (uso embebido por módulo: M1 → ORGANIGRAMA,
   * M2-M6 → DOC/FLUJOGRAMA/PROCESO, M16 → REQUEST).
   */
  artefactoFijo?: ArtefactoTipo;
}

export function AdminWorkflowsTab({ artefactoFijo }: AdminWorkflowsTabProps = {}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const role = user?.role ?? 'MEMBER';
  const isAdmin = ROLES_ADMIN_EDITORS.includes(role);

  const [artefacto, setArtefacto] = useState<ArtefactoTipo>(artefactoFijo ?? 'ORGANIGRAMA');
  const [config, setConfig] = useState<ConfigEtapas | null>(null);
  const [tipos, setTipos] = useState<TipoRolGobernanzaAdmin[]>([]);
  const [puestos, setPuestos] = useState<PuestoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalEtapaId, setModalEtapaId] = useState<string | null>(null);
  const [plantillasEtapaId, setPlantillasEtapaId] = useState<string | null>(null);

  const etapas = config?.etapas ?? [];

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetchEtapasConfig(artefacto),
        fetchTiposRolGobernanza(),
        fetchPuestos({ porPagina: '100' }),
      ]);
      setConfig(r1);
      setTipos(r2.data);
      setPuestos((r3.data ?? r3.datos ?? []) as PuestoItem[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }

  async function handleCambiarRolConfig(
    campo: 'rolGobernanzaIniciador' | 'rolGobernanzaPublicador' | 'rolGobernanzaArchivador',
    rolId: string | null,
  ) {
    await actualizarConfig(artefacto, { [campo]: rolId });
    cargar();
  }

  async function handleToggleFirmaQES(value: boolean) {
    await actualizarConfig(artefacto, { requiereFirmaQES: value });
    cargar();
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artefacto]);

  async function handleCambiarModo(etapaId: string, modo: ModoEtapa) {
    await actualizarEtapa(etapaId, { modo });
    cargar();
  }

  async function handleCambiarAccion(etapaId: string, accion: AccionEtapa) {
    await actualizarEtapa(etapaId, { accion });
    cargar();
  }

  async function handleBorrarEtapa(etapaId: string) {
    if (!confirm('¿Borrar esta etapa? Se eliminarán también las aprobaciones asociadas.')) return;
    await borrarEtapa(etapaId);
    cargar();
  }

  async function handleAnadirEtapa() {
    await crearEtapa(artefacto, 'REVISAR', 'TODOS');
    cargar();
  }

  async function handleQuitarRol(etapaRolId: string) {
    if (!confirm('¿Quitar este rol de la etapa?')) return;
    await quitarRolDeEtapa(etapaRolId);
    cargar();
  }

  async function handleAnadirRol(
    etapaId: string,
    tipoRolId: string,
    puestoFiltroId: string | null,
  ) {
    await anadirRolAEtapa(etapaId, tipoRolId, puestoFiltroId);
    setModalEtapaId(null);
    cargar();
  }

  if (loading) {
    return (
      <div style={{ padding: 32, fontFamily: 'DM Sans, sans-serif', color: C.gray700 }}>
        Cargando workflows…
      </div>
    );
  }

  return (
    <div style={{ padding: 24, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Selector artefacto — oculto si artefactoFijo (embebido por módulo) */}
      {!artefactoFijo && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {ARTEFACTOS.map((a) => (
            <button
              key={a.tipo}
              onClick={() => setArtefacto(a.tipo)}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: `1px solid ${artefacto === a.tipo ? C.azul1 : '#D1D5DB'}`,
                background: artefacto === a.tipo ? C.azul1 : '#fff',
                color: artefacto === a.tipo ? '#fff' : C.azul1,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Help text */}
      <div
        style={{
          padding: '10px 14px',
          background: '#F0F4FF',
          border: '1px solid #BFDBFE',
          borderRadius: 6,
          color: C.azul1,
          fontSize: 12,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        <strong>Workflow multi-etapa</strong>: las etapas se ejecutan en orden. Dentro de cada
        etapa, si <code>modo = TODOS</code> deben aprobar TODAS las personas de los roles
        configurados; si <code>modo = PRIMERO</code> basta con la primera aprobación. El{' '}
        <strong>filtro de puesto</strong> permite forzar un actor concreto cuando un rol está
        asignado a varios puestos (cadena secuencial pura).
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#991B1B',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* Cabecera config: iniciador, publicador, archivador, firma QES */}
      {config && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 20,
            padding: 14,
            background: '#fff',
            border: '1px solid #E2E6ED',
            borderRadius: 8,
          }}
        >
          <ConfigRolField
            label="Iniciador (lanza a revisión)"
            help="Único rol con permiso para ENVIAR_REVISION (BORRADOR → EN_REVISION)."
            value={config.rolGobernanzaIniciador}
            tipos={tipos}
            disabled={!isAdmin}
            onChange={(v) => handleCambiarRolConfig('rolGobernanzaIniciador', v)}
          />
          <ConfigRolField
            label="Publicador"
            help="Único rol con permiso para PUBLICAR (APROBADO → PUBLICADO)."
            value={config.rolGobernanzaPublicador}
            tipos={tipos}
            disabled={!isAdmin}
            onChange={(v) => handleCambiarRolConfig('rolGobernanzaPublicador', v)}
          />
          <ConfigRolField
            label="Archivador"
            help="Único rol con permiso para ARCHIVAR (PUBLICADO → ARCHIVADO)."
            value={config.rolGobernanzaArchivador}
            tipos={tipos}
            disabled={!isAdmin}
            onChange={(v) => handleCambiarRolConfig('rolGobernanzaArchivador', v)}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="firma-qes-toggle"
              checked={config.requiereFirmaQES}
              disabled={!isAdmin}
              onChange={(e) => handleToggleFirmaQES(e.target.checked)}
              style={{ width: 16, height: 16, cursor: isAdmin ? 'pointer' : 'default' }}
            />
            <label
              htmlFor="firma-qes-toggle"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.azul1,
                cursor: isAdmin ? 'pointer' : 'default',
              }}
            >
              Requiere firma electrónica cualificada (QES)
            </label>
          </div>
        </div>
      )}

      {/* Lista etapas */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {etapas.length === 0 && (
          <div style={{ color: C.gray700, fontSize: 13, fontStyle: 'italic' }}>
            No hay etapas configuradas. Añade la primera abajo.
          </div>
        )}
        {etapas.map((etapa) => (
          <div
            key={etapa.id}
            style={{
              border: '1px solid #E2E6ED',
              borderRadius: 8,
              padding: 14,
              background: '#FAFBFC',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700,
                    background: C.azul1,
                    color: '#fff',
                  }}
                >
                  Etapa {etapa.orden}
                </span>
                {isAdmin ? (
                  <select
                    value={etapa.accion}
                    onChange={(e) => handleCambiarAccion(etapa.id, e.target.value as AccionEtapa)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1px solid #D1D5DB',
                      fontSize: 12,
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="REVISAR">
                      {t.admin.workflow.plantillas.selectOptionRevisar}
                    </option>
                    <option value="APROBAR">
                      {t.admin.workflow.plantillas.selectOptionAprobar}
                    </option>
                    <option value="NOTIFICAR">
                      {t.admin.workflow.plantillas.selectOptionNotificar}
                    </option>
                  </select>
                ) : (
                  <strong>{etapa.accion}</strong>
                )}
                {isAdmin ? (
                  <select
                    value={etapa.modo}
                    onChange={(e) => handleCambiarModo(etapa.id, e.target.value as ModoEtapa)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1px solid #D1D5DB',
                      fontSize: 12,
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="TODOS">Modo TODOS</option>
                    <option value="PRIMERO">Modo PRIMERO</option>
                  </select>
                ) : (
                  <span style={{ fontSize: 11, color: C.gray700 }}>· {etapa.modo}</span>
                )}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setModalEtapaId(etapa.id)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: `1px solid ${C.azul2}`,
                      background: '#fff',
                      color: C.azul2,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                    }}
                  >
                    + Rol
                  </button>
                  {etapa.accion === 'NOTIFICAR' && (
                    <button
                      onClick={() => setPlantillasEtapaId(etapa.id)}
                      title={t.admin.workflow.plantillas.actionTooltipNotificar}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 6,
                        border: `1px solid ${C.azul1}`,
                        background: '#fff',
                        color: C.azul1,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: 'inherit',
                      }}
                    >
                      {t.admin.workflow.plantillas.botonAnadirEditor}
                    </button>
                  )}
                  <button
                    onClick={() => handleBorrarEtapa(etapa.id)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: '1px solid #FECACA',
                      background: '#fff',
                      color: '#DC2626',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                    }}
                  >
                    Borrar
                  </button>
                </div>
              )}
            </div>

            {/* Roles de la etapa */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {etapa.roles.length === 0 && (
                <div style={{ color: C.gray700, fontSize: 11, fontStyle: 'italic' }}>
                  Sin roles. {isAdmin ? 'Añade uno con "+ Rol".' : ''}
                </div>
              )}
              {etapa.roles.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    background: '#fff',
                    border: '1px solid #E2E6ED',
                    borderRadius: 6,
                  }}
                >
                  <span style={{ fontSize: 12 }}>
                    <strong style={{ color: C.azul1 }}>{r.tipoRol?.nombre ?? '—'}</strong>
                    {r.puestoFiltro && (
                      <>
                        {' · filtro '}
                        <span style={{ color: C.gray700 }}>
                          {r.puestoFiltro.codigo} {r.puestoFiltro.titulo}
                        </span>
                      </>
                    )}
                    {!r.puestoFiltro && (
                      <span style={{ color: C.gray700, fontStyle: 'italic' }}>
                        {' · todos los puestos del rol'}
                      </span>
                    )}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => handleQuitarRol(r.id)}
                      style={{
                        padding: '3px 8px',
                        borderRadius: 4,
                        border: 'none',
                        background: 'transparent',
                        color: '#DC2626',
                        cursor: 'pointer',
                        fontSize: 11,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {isAdmin && (
          <button
            onClick={handleAnadirEtapa}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: `2px dashed ${C.azul2}`,
              background: 'transparent',
              color: C.azul2,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              marginTop: 8,
            }}
          >
            + Añadir etapa
          </button>
        )}
      </div>

      {/* Modal añadir rol */}
      {modalEtapaId && (
        <ModalAnadirRol
          etapaId={modalEtapaId}
          tipos={tipos}
          puestos={puestos}
          onClose={() => setModalEtapaId(null)}
          onSubmit={handleAnadirRol}
        />
      )}

      {/* Modal editor plantillas notif (0658a) */}
      {plantillasEtapaId && (
        <NotifPlantillaEditor
          etapaId={plantillasEtapaId}
          onClose={() => setPlantillasEtapaId(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Field rol gobernanza (cabecera config)
// =============================================================================

interface ConfigRolFieldProps {
  label: string;
  help: string;
  value: string | null;
  tipos: TipoRolGobernanzaAdmin[];
  disabled?: boolean;
  onChange: (rolId: string | null) => void;
}

function ConfigRolField({ label, help, value, tipos, disabled, onChange }: ConfigRolFieldProps) {
  return (
    <div>
      <label
        style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: C.azul1 }}
      >
        {label}
      </label>
      <select
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
        style={{
          width: '100%',
          padding: '7px 10px',
          borderRadius: 6,
          border: '1px solid #D1D5DB',
          fontSize: 12,
          fontFamily: 'inherit',
          background: disabled ? '#F3F4F6' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="">— Sin asignar —</option>
        {tipos.map((t) => (
          <option key={t.id} value={t.id}>
            {t.nombre}
          </option>
        ))}
      </select>
      <small style={{ display: 'block', color: '#6B7280', fontSize: 10, marginTop: 3 }}>
        {help}
      </small>
    </div>
  );
}

// =============================================================================
// Modal añadir rol
// =============================================================================

interface ModalProps {
  etapaId: string;
  tipos: TipoRolGobernanzaAdmin[];
  puestos: PuestoItem[];
  onClose: () => void;
  onSubmit: (etapaId: string, tipoRolId: string, puestoFiltroId: string | null) => Promise<void>;
}

function ModalAnadirRol({ etapaId, tipos, puestos: _puestosAll, onClose, onSubmit }: ModalProps) {
  void _puestosAll; // 0642b: ya no usamos lista global; ahora dinamica por rol
  const [tipoRolId, setTipoRolId] = useState<string>('');
  const [puestoFiltroId, setPuestoFiltroId] = useState<string>('');
  const [puestosDelRol, setPuestosDelRol] = useState<PuestoItem[]>([]);
  const [loadingPuestos, setLoadingPuestos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 0642b: al cambiar el rol seleccionado, recargar puestos asignados a ESE rol.
  // Si no hay rol seleccionado, vaciamos la lista (el dropdown sale solo
  // con la opción "Cualquier puesto del rol"). Reset puestoFiltroId para
  // evitar dejar un id stale del rol anterior.
  useEffect(() => {
    setPuestoFiltroId('');
    if (!tipoRolId) {
      setPuestosDelRol([]);
      return;
    }
    let cancelled = false;
    setLoadingPuestos(true);
    fetchPuestosDeTipoRol(tipoRolId)
      .then((r) => {
        if (cancelled) return;
        setPuestosDelRol(r.data as PuestoItem[]);
      })
      .catch(() => {
        if (!cancelled) setPuestosDelRol([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPuestos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tipoRolId]);

  async function handleOk() {
    if (!tipoRolId) return;
    setSubmitting(true);
    try {
      await onSubmit(etapaId, tipoRolId, puestoFiltroId || null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: 24,
          minWidth: 420,
          maxWidth: 520,
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        <h3 style={{ margin: 0, marginBottom: 16, color: C.azul1 }}>Añadir rol a la etapa</h3>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Tipo de rol gobernanza *
          </label>
          <select
            value={tipoRolId}
            onChange={(e) => setTipoRolId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          >
            <option value="">— Selecciona un rol —</option>
            {tipos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nombre} ({t.codigo})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Filtro por puesto (opcional)
          </label>
          <select
            value={puestoFiltroId}
            disabled={!tipoRolId || loadingPuestos}
            onChange={(e) => setPuestoFiltroId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              fontFamily: 'inherit',
              background: !tipoRolId ? '#F3F4F6' : '#fff',
              cursor: !tipoRolId ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="">— Cualquier puesto del rol —</option>
            {puestosDelRol.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} {p.titulo}
              </option>
            ))}
          </select>
          <small style={{ display: 'block', color: '#6B7280', fontSize: 11, marginTop: 4 }}>
            {!tipoRolId
              ? 'Selecciona primero un tipo de rol para ver los puestos disponibles.'
              : loadingPuestos
                ? 'Cargando puestos del rol…'
                : puestosDelRol.length === 0
                  ? 'Este rol no tiene puestos asignados todavía. Asigna el rol a un puesto desde Roles de Gobernanza.'
                  : 'Si se especifica, solo el ocupante de ESE puesto activa la etapa (cadena pura).'}
          </small>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleOk}
            disabled={submitting || !tipoRolId}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: 'none',
              background: tipoRolId ? C.azul1 : '#9CA3AF',
              color: '#fff',
              cursor: tipoRolId ? 'pointer' : 'not-allowed',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Guardando…' : 'Añadir rol'}
          </button>
        </div>
      </div>
    </div>
  );
}
